import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { jsonApi } from '@/lib/jsonApi';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Platform-admin only: route a historical response into the QA queue.
export default function QaMigrate({ response, onMigrated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flip, setFlip] = useState(true);
  const rid = response.public_id || response.response_id;
  const run = async () => {
    setBusy(true);
    try {
      const d = await jsonApi(`/qa/migrate-response/${encodeURIComponent(response.response_id || rid)}`, 'POST', { update_parent_survey: flip, tat_hours_from_now: 24 });
      if (d.response_migrated) toast.success(`Sent to QA queue${d.survey_flipped ? ' · survey set to Mystery Shopping' : ''}`);
      else toast.info(d.message || 'Already in QA queue');
      onMigrated({ survey_type: 'mystery_shopping', qa_status: 'submitted', tat_deadline: d.tat_deadline });
      setOpen(false);
    } catch (e) { toast.error(e.message || 'Migration failed'); } finally { setBusy(false); }
  };
  if (response.survey_type === 'mystery_shopping') {
    return (
      <div className="flex items-center justify-between gap-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl px-4 py-2.5" data-testid="qa-migrate-done">
        <span className="text-xs font-medium text-emerald-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> In QA queue · {response.qa_status || 'submitted'}</span>
        <Button size="sm" variant="outline" onClick={() => { window.location.href = `/qa-review/${response.response_id || rid}`; }} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="qa-open-review-btn">Open QA review</Button>
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center justify-between gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl px-4 py-2.5" data-testid="qa-migrate-strip">
        <div className="text-xs text-[#1E3A8A] flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-[#1A3E6F]" /><span><b>Not yet in QA queue.</b> Send this response to QA for review. Answers stay untouched.</span></div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white shrink-0" data-testid="qa-migrate-btn">Send to QA queue</Button>
      </div>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="qa-migrate-modal">
          <DialogHeader><DialogTitle className="font-['Montserrat'] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0EA5E9]" /> Send to QA queue</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Response <span className="font-mono text-xs text-[#0F172A]">{rid}</span> will be tagged as a Mystery Shopping FIND (QA status Submitted, TAT 24 hours). All answers are preserved.</p>
          <label className="flex items-start gap-2 cursor-pointer bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 text-xs" data-testid="qa-migrate-flip-survey">
            <input type="checkbox" checked={flip} onChange={(e) => setFlip(e.target.checked)} className="mt-0.5" />
            <span><b className="text-[#0F172A]">Also flip the parent survey to Mystery Shopping</b><br /><span className="text-[#64748B]">Every future submission to this survey will auto-enter the QA queue.</span></span>
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="qa-migrate-cancel">Cancel</Button>
            <Button onClick={run} disabled={busy} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="qa-migrate-confirm">{busy ? 'Sending…' : 'Send to QA queue'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
